import { refreshAccessToken } from './auth-config';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8081';

export async function authFetch(path: string, options?: RequestInit): Promise<Response> {
  let token = sessionStorage.getItem('access_token');

  if (!token) {
    window.location.href = '/login';
    throw new Error('Not authenticated');
  }

  const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  if (Date.now() >= (payload.exp - 30) * 1000) {
    const rt = sessionStorage.getItem('refresh_token');
    if (rt) {
      try {
        const res = await refreshAccessToken(rt);
        sessionStorage.setItem('access_token', res.accessToken);
        sessionStorage.setItem('refresh_token', res.refreshToken);
        if (res.idToken) sessionStorage.setItem('id_token', res.idToken);
        token = res.accessToken;
      } catch {
        sessionStorage.clear();
        window.location.href = '/login';
        throw new Error('Session expired');
      }
    }
  }

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  return fetch(url, {
    ...options,
    headers: {
      ...options?.headers,
      Authorization: `Bearer ${token}`,
    },
  });
}

export interface PaymentResponse {
  id: string;
  billReference: string;
  bank: string;
  amountCents: number;
  currency: string;
  status: string;
  merchantName: string;
  qrPayload: string;
  qrImageBase64: string;
  qrExpiresAt: string;
  createdAt: string;
  updatedAt: string;
  paidAt: string | null;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface CreatePaymentRequest {
  bank: string;
  amountCents: number;
  billReference?: string;
  merchantName: string;
  expiresInMinutes?: number;
}

export interface FetchPaymentsParams {
  status?: string;
  bank?: string;
  page?: number;
  size?: number;
}

export async function fetchPayments(
  params: FetchPaymentsParams
): Promise<PageResponse<PaymentResponse>> {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.bank) query.set('bank', params.bank);
  query.set('page', String(params.page ?? 0));
  query.set('size', String(params.size ?? 20));

  const res = await authFetch(`/api/v1/payment-requests?${query}`);

  if (!res.ok) {
    throw new Error(`Failed to fetch payments: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

export async function fetchPayment(id: string): Promise<PaymentResponse> {
  const res = await authFetch(`/api/v1/payment-requests/${id}`);

  if (!res.ok) {
    throw new Error(`Failed to fetch payment ${id}: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

export async function createPayment(data: CreatePaymentRequest): Promise<PaymentResponse> {
  const res = await authFetch(`/api/v1/payment-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const errorBody = await res.text();
    throw new Error(`Failed to create payment: ${res.status} ${errorBody}`);
  }

  return res.json();
}

export async function cancelPayment(id: string): Promise<PaymentResponse> {
  const res = await authFetch(`/api/v1/payment-requests/${id}/cancel`, {
    method: 'POST',
  });

  if (!res.ok) {
    throw new Error(`Failed to cancel payment ${id}: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

export const PAYMENT_STATUSES = [
  'PENDING',
  'PAID',
  'COMPLETED',
  'PAID_CALLBACK_FAILED',
  'EXPIRED',
  'CANCELLED',
  'FAILED'
] as const;

export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const BANKS = ['DBS', 'UOB'] as const;
export type Bank = (typeof BANKS)[number];
